import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, Eye, AlertTriangle } from "lucide-react";

// Very lightweight markdown-ish renderer for the structured AI output.
function renderAnalysis(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const out = [];
  let buffer = [];

  const flush = (key) => {
    if (buffer.length === 0) return;
    out.push(
      <p key={`p-${key}`} className="text-sm leading-relaxed text-stone-300 mb-4">
        {buffer.join(" ")}
      </p>
    );
    buffer = [];
  };

  lines.forEach((line, i) => {
    const t = line.trim();
    if (t.startsWith("## ")) {
      flush(`b-${i}`);
      out.push(
        <h4
          key={`h-${i}`}
          className="font-mono-head text-xs uppercase tracking-[0.25em] text-[#E60000] mt-6 mb-2 flex items-center gap-2"
        >
          <span className="w-3 h-px bg-[#E60000]" />
          {t.replace(/^##\s+/, "")}
        </h4>
      );
    } else if (t.startsWith("- ") || t.startsWith("* ")) {
      flush(`l-${i}`);
      out.push(
        <div key={`li-${i}`} className="text-sm leading-relaxed text-stone-300 mb-2 pl-4 border-l border-stone-700">
          {t.replace(/^[-*]\s+/, "")}
        </div>
      );
    } else if (t === "") {
      flush(`e-${i}`);
    } else {
      buffer.push(t);
    }
  });
  flush("end");
  return out;
}

export default function HiddenTruthsPanel({ storyId }) {
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.post(`/news/${storyId}/analyze`)
      .then((r) => { if (!cancelled) setAnalysis(r.data.analysis); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Analysis failed. Try again."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [storyId]);

  return (
    <div
      data-testid={`hidden-truth-panel-${storyId}`}
      className="mt-6 bg-stone-900 text-stone-100 p-6 md:p-8 border-l-4 border-[#E60000] relative overflow-hidden"
    >
      <div className="absolute inset-0 hidden-truth-stripes pointer-events-none opacity-30" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-4">
          <Eye className="w-4 h-4 text-[#E60000]" />
          <h3 className="font-mono-head text-sm uppercase tracking-[0.3em] text-[#E60000]">
            Hidden Truths · AI Analysis
          </h3>
        </div>

        {loading && (
          <div className="flex items-center gap-3 text-stone-400 font-mono-head text-xs uppercase tracking-widest py-6">
            <Loader2 className="w-4 h-4 animate-spin" />
            Decrypting narrative · Probing sources · Triangulating bias…
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 text-stone-300 text-sm">
            <AlertTriangle className="w-4 h-4 text-[#E60000] mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && analysis && (
          <div className="prose prose-invert max-w-none">{renderAnalysis(analysis)}</div>
        )}

        {!loading && !error && analysis && (
          <div className="mt-6 pt-4 border-t border-stone-700 font-mono-head text-[10px] uppercase tracking-[0.25em] text-stone-500">
            ⚠ Alternative angles are speculative · Always verify with primary sources
          </div>
        )}
      </div>
    </div>
  );
}
